import {
  breadcrumbsSchema,
  pageSchema,
  productSchema,
  siteSchema,
  straplinesSchema,
} from '~/schemas'
import { mapStorefrontView } from '~/schemas/storefront-view'
import { turnstileSetting } from './site'
import { fetchApi, getFetchApiErrorStatus, isFetchApiError } from './helpers'
import { useActiveEntity } from '~/composables/useActiveEntity'

const booleanSetting = (value: unknown, fallback = true) => {
  if (value === undefined || value === null || value === '') return fallback
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value !== 0

  return !['0', 'false', 'no', 'off'].includes(String(value).toLowerCase())
}

export const fetchInfoData = async ({ domain, path }: {
  domain: string
  path: string
}) => {
  try {
    // Bind composables before the first await; async context can be lost after awaits.
    const nuxtApp = useNuxtApp()
    const activeEntity = useActiveEntity()
    const siteStore = useSiteStore()

    const response: any = await fetchApi(`/info`, {
      query: { domain, path }
    })

    if (response.redirectTo) {
      return await nuxtApp.runWithContext(() =>
        navigateTo(response.redirectTo, { redirectCode: 301, external: true }),
      )
    }


    const site = response.site
      ? siteSchema.parse({
        id: response.site.data?.id,
        name: response.site.data?.name,
        phoneNumber: response.site.settings?.data?.main_phone,
        emailAddress: response.site.settings?.data?.mail,
        address: response.site.settings?.data?.address,
        googleAnalyticsId: response.site.settings?.data?.ga_key,
        googleGtmId: response.site.settings?.data?.google_gtm_id,
        scriptSources: response.site.settings?.data?.script_sources,
        scriptBodies: response.site.settings?.data?.script_bodies,
        facebookAnalyticsId: response.site.settings?.data?.fb_key,
        facebookLink: response.site.settings?.data?.useful_link_facebook,
        instagramLink: response.site.settings?.data?.useful_link_instagram,
        tiktokLink: response.site.settings?.data?.useful_link_tiktok,
        youtubeLink: response.site.settings?.data?.useful_link_youtube,
        trustPilotLink: response.site.settings?.data?.useful_link_trustpilot,
        tripAdvisorLink: response.site.settings?.data?.useful_link_tripadvisor,
        ticketSupplierDescription: response.site.settings?.data?.ticket_supplier_description,
        hotelsEnabled: booleanSetting(response.site.settings?.data?.hotels_enabled, true),
        turnstile: turnstileSetting(response.site.settings?.data),
        currency: {
          code: response.site.currencies?.[0]?.data?.code,
          roundUp: response.site.currencies?.[0]?.data?.round_up,
          name: response.site.currencies?.[0]?.data?.name
        },
        links: {
          terms: response.site.page_links?.terms
        }
      }) : undefined

    if (site) {
      siteStore.setSiteData(site)
    }

    const hasStraplinesField = Object.prototype.hasOwnProperty.call(response, 'straplines')

    const straplines = hasStraplinesField
      ? (
        response.straplines == null
          ? null
          : straplinesSchema.parse(
            (Array.isArray(response.straplines) ? response.straplines : []).map((item: any) => ({
              id: item.data?.id,
              name: item.data?.name,
              text: item.data?.text,
              alignment: item.data?.alignment,
              backgroundColour: item.data?.background_colour,
              textColour: item.data?.text_colour,
              linkHoverColour: item.data?.link_hover_colour,
              position: item.data?.position,
            })),
          )
      )
      : siteStore.straplines

    if (hasStraplinesField) {
      siteStore.setStraplines(straplines)
    }

    const page = response.page
      ? pageSchema.parse({
        id: response.page.data?.id,
        hasChild: response.page.extra?.has_child || false,
        parentId: response.page.data?.parent_id,
        image: response.page.media?.[0]?.data?.url,
        predefinedSearchCriteriaOptionId: response.page.extra?.predefined_search_criteria_option_id || null,
        media: response.page.media.map((item: any) => ({ ...item.data, ...item.extra })),
        path: response.page.extra.url?.data?.url,
        pathAlternatives: Array.isArray(response?.page?.urls) && response.page.urls.length
          ? response.page.urls.map((item: any) => ({
            path: item.data.url,
            siteId: item.data.site_id,
          }))
          : [],
        title:
          response.page.extra.translation?.data?.h1_title ||
          response.page.extra.translation?.data?.name,
        description: response.page.extra.translation?.data?.description,
        body: response.page.extra.translation?.data?.body,
        additionalDescription:
        response.page.extra.translation?.data?.additional_description,
        additionalNotes: response.page.extra.translation?.data?.additional_notes,
        tags: response.page.tags
          ?.map((tag: Record<string, any>) => ({
            id: tag.data?.id,
            name: tag.data?.name
          }))
          .reduce((acc: Record<string, any>[], tag: Record<string, any>) => {
            if (!acc.find((t) => t.id === tag.id)) acc.push(tag)
            return acc
          }, []),
        favourited: response.page.extra?.favourited,
        seoTitle: response.page.extra.translation?.data?.seo_title,
        seoDescription: response.page.extra.translation?.data?.seo_description,
        seoKeywords: response.page.extra.translation?.data?.seo_keywords,
        templateAlias: response.page.template?.data?.alias,
        content: response.page.content,
        views: (response.views || []).map(mapStorefrontView),
      })
      : undefined

    const product = response.product
      ? productSchema.parse({
        id: response.product.data?.id,
        parentId: response.product.data?.parent_id,
        type: response.product.data?.type,
        image:
          response.product.extra.default_image?.data?.url ||
          response.product.media?.[0]?.data?.url
          ,
        media: response.product.media.length > 0
          ? response.product.media.map((item: any) => ({ ...item.data, ...item.extra }))
          : response.product.page?.media?.map((item: any) => ({ ...item.data, ...item.extra })),
        path: response.product.extra.url?.data?.url,
        pathAlternatives: Array.isArray(response?.product?.urls) && response.product.urls.length
          ? response.product.urls.map((item: any) => ({
            path: item.data.url,
            siteId: item.data.site_id,
          }))
          : [],
        title:
          response.product.extra.translation?.data?.h1_title ||
          response.product.extra.translation?.data?.name,
        name: response.product.extra.translation?.data?.name ||
          response.product.extra.translation?.data?.h1_title,
        intro:
            response.product.data?.type === 'football_match'
                ? (response.product.extra.translation?.data?.intro)
                : (response.product.extra.translation?.data?.intro ||
                    response.product.page?.extra.translation?.data?.default_product_intro ||
                    (response.product.page?.extra.translation?.data?.description_disabled
                        ? undefined
                        : response.product.page?.extra.translation?.data?.description)),
        description: response.product.extra.translation?.data?.description,
        source: response.product.extra.source_safe_name,
        ticketSupplierDescription: response.product?.extra.translation?.data?.ticket_supplier_description ||
          response.product.page?.extra.translation?.data?.ticket_supplier_description ||
          site?.ticketSupplierDescription,
        body: response.product.extra.translation?.data?.body,
        notes: response.product.extra.translation?.data?.notes,
        additionalDescription:
        response.product.page?.extra.translation?.data?.additional_description,
        additionalNotes:
        response.product.page?.extra.translation?.data?.additional_notes,
        startPrice: response.product.extra.starting_price,
        startDate: response.product.data?.start_date,
        startTime: response.product.data?.start_time,
        endDate: response.product.data?.end_date,
        endTime: response.product.data?.end_time,
        hasPerformances: response.product.data?.has_performances,
        soldOut: getSoldOutData(response.product),
        url: response.product.extra.url?.data?.url,
        venue: {
          name: response.product.venue?.extra?.translation?.data?.name,
          description: response.product.venue?.extra?.translation?.data?.description,
          address: response.product.venue?.extra?.translation?.data?.address,
          externalId: response.product.venue?.data?.external_api_id,
          timezone: response.product.venue?.data?.timezone,
          googleMapsLink: response.product.venue?.data?.google_map_url,
          stadiumImage: response.product.venue?.media?.find(
            (item: Record<string, any>) =>
              item?.data?.collection_name === 'stadium'
          )?.data?.url,
          seatWidgetId: response.product.options?.data?.venue_external_id,
          seatPlans: response.product.venue?.seat_plans,
        },
        faqs: response.product.faqItems?.map((faq: Record<string, any>) => ({
          position: faq.data?.position,
          question: faq.translation?.data?.question,
          answer: faq.translation?.data?.answer,
          slug: faq.translation?.data?.slug
        })),
        competitions: response.product?.competitions?.map((i: any) => ({id: i.data.id, name: i.data.name})),
        extra: {
          margin: response.product.extra.margin,
          exchangeRate: response.product.extra.exchange_rate,
          maxReservationTicketCount: response.product.options?.data?.max_reservation_ticket_count,
        },
        variations: response.product.variations?.map(
          (variation: Record<string, any>) => ({
            externalId: variation.data?.external_id,
            name: variation.translation?.data?.name,
            description: variation.translation?.data?.description
          })
        ),
        keyFeatureLabels: (response.product.keyFeatureLabels || []).map((item: Record<string, any>) => ({
          name: item.extra.translation.data.name,
          colour: item.data.colour || '#E0F0FF'
        })),
        views: (response.views || []).map(mapStorefrontView),
        footballClubPages: (response.product.football_club_pages || []).map((item: Record<string, any>) => ({
          id: item.data.id,
          title:
            item.extra.translation?.data?.h1_title ||
            item.extra.translation?.data?.name,
          description: item.extra.translation?.data?.description,
        })),
        mandatoryHotel: !!response.product.options?.data?.mandatory_hotel,
        favourited: response.product.extra.favourited,
        seoTitle: response.product.extra.translation?.data?.seo_title,
        seoDescription: response.product.extra.translation?.data?.seo_description,
        seoKeywords: response.product.extra.translation?.data?.seo_keywords
      })
      : undefined

    if (page) {
      activeEntity.value = {
        type: 'page',
        entity: page,
        links: {}
      }
    } else if (product) {
      activeEntity.value = {
        type: 'product',
        entity: product,
        links: {},
      }
    } else {
      activeEntity.value = {
        type: 'undefined',
        entity: null,
        links: {}
      }
    }
    const breadcrumbs = breadcrumbsSchema.parse(response.breadcrumb)

    return { page, product, breadcrumbs, straplines }
  } catch (error) {
    if (isFetchApiError(error)) {
      if ([404, 422].includes(getFetchApiErrorStatus(error) ?? 0)) {
        throw createError({
          statusCode: 404,
          statusMessage: 'Page not found'
        })
      }
    }

    throw error
  }
}

function getSoldOutData(product: Record<string, any>) {
  const soldOut = {
    message: null,
    modal: Boolean(product.translation?.data?.sold_out_modal ||
      product.page?.translation?.data?.sold_out_modal || 0),
  }

  if (product.translation?.data?.sold_out_behaviour === 1) {
    soldOut.message = product.page?.translation?.data?.sold_out_message
    soldOut.modal = Boolean(product.page?.translation?.data?.sold_out_modal || 0)
  } else if (product.translation?.data?.sold_out_behaviour === 2) {
    soldOut.message = product.translation?.data?.sold_out_message
    soldOut.modal = Boolean(product.translation?.data?.sold_out_modal || 0)
  } else if (product.translation?.data?.sold_out_behaviour === 3) {
    soldOut.modal = false
  }

  return soldOut
}

export type InfoData = Awaited<ReturnType<typeof fetchInfoData>>
